import { faBellSlash } from "@fortawesome/pro-regular-svg-icons/faBellSlash";
import { faCircleEllipsis } from "@fortawesome/pro-regular-svg-icons/faCircleEllipsis";
import { faShareNodes } from "@fortawesome/pro-regular-svg-icons/faShareNodes";
import { faBell } from "@fortawesome/pro-solid-svg-icons/faBell";
import { faUserTag } from "@fortawesome/pro-solid-svg-icons/faUserTag";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { CorePermissions } from "app/appPermissions";
import { MprcButton, MprcIconButton, MprcIconFa, MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { IconFollow } from "components/icons";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { MprcDropdownBuildGroupedItems, MprcDropdownItem, MprcIconButtonDropdown } from "components/MprcIconButtonDropdown";
import { MprcRequestSnackbarErrorReport } from "components/MprcRequestErrorReport";
import { ChannelAutoRegisterMode, FeedChannelHeaderResponse } from "generated/CoreApi_v1";
import HtmlUtils from "lib/HtmlUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { DashboardPaths } from "routes/DashboardRouter";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { UserSelectorDrawer, UserSelectorDrawerResult } from "views/foundation/user/drawers/UserSelectorDrawer";
import { DeleteChannel } from "views/knowledge/channels/Home/ListChannels/DeleteChannel";
import { JoinChannels } from "views/knowledge/channels/Home/ListChannels/JoinChannels";
import { SubscribeChannel, SubscribeChannelProps, UnsubscribeChannel } from "views/knowledge/subcriptions/Home/SubscriptionSignup/ChannelSubscriptions";

////////////////////////////////////////////////////////////////////////////////

interface IProps {
    channel: FeedChannelHeaderResponse | null;
    onSuccess?: () => void;
}

////////////////////////////////////////////////////////////////////////////////

export const ShareChannelButton: React.FC<IProps> = (props) => {

    const { channelId } = props.channel!;

    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation("knowledge");

    type ShareButtonOptions = "copyLink";

    ///
    const copyLink = () => {
        const host = window.location.protocol + "//" + window.location.host;
        const channelUrl = `${host}${KnowledgePaths.FeedChannel(channelId)}`;

        HtmlUtils.copyToClipboard(channelUrl)
            .then(() => enqueueSnackbar(t("Feed.Card.Common.CopyLinkSuccessMessage"), { variant: "success" }))
            .catch(() => enqueueSnackbar(t("Feed.Card.Common.CopyLinkFailMessage"), { variant: "error" }));
    };

    ///
    const getMenuItems = (): MprcDropdownItem<ShareButtonOptions>[] => {
        var group1: MprcDropdownItem<ShareButtonOptions>[] = [
            { text: t("Feed.Card.Common.CopyLinkAction"), value: "copyLink" }
        ];

        return MprcDropdownBuildGroupedItems([group1]);
    };

    ///
    const handleMenuItem = (option: ShareButtonOptions) => {
        switch (option) {
            case "copyLink":
                copyLink();
                break;
        }
    };

    return (
        <Typography variant="iconText" component="div">
            <MprcIconButtonDropdown
                color="paper"
                icon={<FontAwesomeIcon icon={faShareNodes} fixedWidth />}
                items={getMenuItems()}
                onChange={handleMenuItem}
                separator="blank"
                title={t("Feed.Card.Common.ShareButtonTooltip")}
            />
        </Typography>
    );
};


////////////////////////////////////////////////////////////////////////////////

type ActionsOptions = "inviteUser" | "manageMembers" | "manageChannel" | "managePublication" | "analysis" | "joinChannel" | "delete";

type MenuItemData = { key: ActionsOptions, label: React.ReactNode; };

export const ActionChannelButton: React.FC<IProps> = (props) => {

    const { channelId, name, allowInviteUser, allowManageMember, allowManageChannel, allowManagePublication } = props.channel!;

    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation("knowledge");

    const api = useCoreApi();
    const { permissionExplorer } = useSession();
    const navigate = useNavigate();

    const [posting, setPosting] = useState<boolean>(false);
    const [dialog, setDialog] = useState<any>();
    const [anchor, setAnchor] = React.useState<Element | null>(null);

    const groupOptions: Array<Array<MenuItemData>> = [];

    const groupItems1: MenuItemData[] = [];
    if (allowInviteUser) groupItems1.push({ key: "inviteUser", label: t("Feed.Channel.Actions.InviteUser") });
    if (groupItems1.length > 0) groupOptions.push(groupItems1);

    const groupItems2: MenuItemData[] = [];
    if (allowManageMember) groupItems2.push({ key: "manageMembers", label: t("Feed.Channel.Actions.ManageMembers") });
    if (allowManageChannel) groupItems2.push({ key: "manageChannel", label: t("Feed.Channel.Actions.ManagerChannel") });
    if (allowManagePublication) groupItems2.push({ key: "managePublication", label: t("Feed.Channel.Actions.ManagePublication") });
    if (groupItems2.length > 0) groupOptions.push(groupItems2);

    const groupItems3: MenuItemData[] = [];
    if (permissionExplorer.hasOne([CorePermissions.KnowledgeViewDashboard])) groupItems3.push({ key: "analysis", label: t("Feed.Channel.Actions.Analysis") });
    if (groupItems3.length > 0) groupOptions.push(groupItems3);

    // Opções do menu
    const menuItems = useMemo(() => {
        var arr: React.ReactNode[] = [];

        groupOptions.forEach((g, gindex) => {
            g.forEach((v) => {
                arr.push(<MenuItem
                    key={v.key}
                    onClick={
                        () => {
                            handleMenuItem(v.key);
                        }
                    }
                >
                    {v.label}
                </MenuItem>);
            });

            if (gindex < groupOptions.length - 1)
                arr.push(<Box sx={{ mt: 1.5 }} key={`${gindex}_divider`} />);
        });

        return arr;
    }, [groupOptions]);

    ///
    const inviteUser = async (user: UserSelectorDrawerResult) => {

        setPosting(true);
        try {
            var response = await api.v1.channelManagement.inviteUser({ channelId, userId: user.userId });
            if (response.data === true) enqueueSnackbar(t("Feed.Channel.Invite.SucessMessage"), { variant: "success" });
            else enqueueSnackbar("", { variant: "error" });
        }
        catch (e) {
            MprcRequestSnackbarErrorReport(e);
        }
        finally {
            setPosting(false);
        }
    };

    ///
    const handleMenuItem = (option: ActionsOptions) => {

        setAnchor(null);

        switch (option) {
            case "inviteUser":
                setDialog(<UserSelectorDrawer
                    onClose={
                        (users) => {
                            if (users) inviteUser(users[0]);
                            setDialog(null);
                        }}
                />);
                break;
            case "manageMembers":
                navigate(KnowledgePaths.BuildEditChannelMembers(channelId));
                break;
            case "manageChannel":
                navigate(KnowledgePaths.BuildEditChannel(channelId));
                break;
            case "managePublication":
                navigate(KnowledgePaths.ManagementPublication, {
                    state: {
                        openForm: {
                            channelId,
                            name
                        }
                    }
                });
                break;
            case "joinChannel":
                setDialog(
                    <JoinChannels
                        channelId={channelId}
                        name={name}
                        onClose={(channelDestinyId) => {
                            setDialog(null);
                            if (channelDestinyId) {
                                navigate(KnowledgePaths.FeedChannel(channelDestinyId));
                            }
                        }
                        }
                    />
                );
                break;
            case "delete":
                setDialog(
                    <DeleteChannel
                        channelId={channelId}
                        channelName={name}
                        onClose={(success) => {
                            if (success) {
                                navigate(KnowledgePaths.FeedStartup);
                            }

                            setDialog(null);
                        }}
                    />
                );
                break;
            case "analysis":
                navigate(DashboardPaths.KnowledgePanel, { state: { channel: { id: channelId, name: name } } });
                break;
        }
    };

    ////////////////////////////////////////////////////////////////////////////////

    return (
        <>
            <MprcRequestIndicator type="post" visible={posting} />
            {dialog}
            {
                menuItems.length > 0
                    ?
                    <MprcFeedTypography variant="action" color="white">
                        <IconButton color="inherit" sx={{ pl: 0, pr: 0 }} onClick={(e) => setAnchor(e.currentTarget)}>
                            <FontAwesomeIcon icon={faCircleEllipsis} fixedWidth />
                        </IconButton>
                        <Menu
                            anchorEl={anchor}
                            open={anchor != null}
                            onClose={() => setAnchor(null)}
                        >
                            {menuItems}
                        </Menu>
                    </MprcFeedTypography>
                    : null
            }
        </>
    );
};

////////////////////////////////////////////////////////////////////////////////

export const SubscribeChannelButton: React.FC<SubscribeChannelProps> = (props) => {

    const { channel, onComplete } = props;

    const { t } = useTranslation("knowledge");

    const [dialog, setDialog] = useState<any>();

    ///
    const handleClick = () => {
        setDialog(<SubscribeChannel
            channel={channel}
            onComplete={(success) => {
                onComplete(success);
                setDialog(null);
            }}
        />);
    };

    return (
        <>
            {dialog}

            <MprcButton
                text={t("Feed.Channel.SubscribeButtonText")}
                title={t("Feed.Channel.SubscribeButtonTitle")}
                color={"primary"}
                variant="contained"
                fullWidth
                startIcon={<IconFollow />}
                onClick={handleClick}
            />
        </>
    );
};

////////////////////////////////////////////////////////////////////////////////

export const UnsubscribeChannelButton: React.FC<SubscribeChannelProps & { isOwnerChannel: boolean; }> = (props) => {

    const { channel, onComplete, isOwnerChannel } = props;
    const { t } = useTranslation("knowledge");
    const [dialog, setDialog] = useState<any>();

    const { enqueueSnackbar } = useSnackbar();
    ///
    const handleClick = () => {

        if (channel.indirectMember || channel.autoRegisterMode == ChannelAutoRegisterMode.Forbidden) {
            enqueueSnackbar(t("Feed.Channel.ForbiddenUnsubscribeMessage"), { variant: "error" });
            return;
        }
        else if (isOwnerChannel) {
            enqueueSnackbar(t("Feed.Channel.OwnerUnsubscribeMessage"), { variant: "error" });
            return;
        }

        setDialog(<UnsubscribeChannel
            channel={channel}
            onComplete={(success) => {
                onComplete(success);
                setDialog(null);
            }}
        />);
    };

    return (
        <>
            {dialog}

            <MprcButton
                text={t("Feed.Channel.SubscribedButtonText")}
                title={t("Feed.Channel.SubscribedButtonTitle")}
                color={"success"}
                variant="contained"
                fullWidth
                startIcon={<IconFollow />}
                onClick={channel.readonly ? undefined : () => handleClick()}
                sx={{ cursor: channel.readonly ? "not-allowed" : "pointer" }}
            />
        </>
    );
};

export const ToggleMutedChannelButton: React.FC<IProps> = (props) => {

    const { channel, onSuccess } = props;

    const { t } = useTranslation("knowledge");
    const api = useCoreApi();
    const { enqueueSnackbar } = useSnackbar();

    const [posting, setPosting] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>(null);

    ///
    const doToggleMuted = async () => {
        setPosting(true);
        try {
            var result = await api.v1.knowledgeSubcription.toggleMutedChannel({ channelId: channel!.channelId });

            if (result) {
                if (channel!.muted) {
                    enqueueSnackbar(t("Feed.Channel.MutedMessage"), { variant: "success" });
                }
                else {
                    enqueueSnackbar(t("Feed.Channel.UnmutedMessage"), { variant: "success" });
                }
            }

            if (onSuccess) onSuccess();
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setPosting(false);
        }
    };

    const handleClick = () => {
        doToggleMuted();
    };

    return (
        <>
            <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

            <MprcIconButton
                title={channel!.muted ? t("Feed.Channel.UnmutedButtonTitle") : t("Feed.Channel.MutedButtonTitle")}
                color={channel!.muted ? "muted" : "success"}
                disabled={posting}
                onClick={handleClick}
            >
                <MprcIconFa {...props} icon={channel!.muted ? faBellSlash : faBell} />
            </MprcIconButton>
        </>
    );
};

export const PendingApprovalChannelButton: React.FC = () => {

    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();

    const handleClick = () => {
        enqueueSnackbar(t("Feed.Channel.PendingApprovalButtonTitle"), { variant: "warning" });
    };

    return (
        <MprcButton
            text={t("Feed.Channel.PendingApprovalButtonText")}
            title={t("Feed.Channel.PendingApprovalButtonTitle")}
            color="warning"
            variant="contained"
            startIcon={<FontAwesomeIcon icon={faUserTag} />}
            onClick={handleClick}
        />
    );
};
